import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { chatbotApi, formatApiError } from "../lib/api";
import ReactMarkdown from "react-markdown";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { SectionCard } from "../components/ui/page-shell";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ArrowLeft, Play, Settings, Database, MessageCircle, ExternalLink, Copy, Check } from "lucide-react";
import { toast } from "sonner";

// Markdown components for bot messages
const MarkdownComponents = {
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  h1: ({ children }) => <h1 className="text-lg font-bold mb-2 mt-3 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-bold mb-2 mt-3 first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-bold mb-1 mt-2 first:mt-0">{children}</h3>,
  ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
  li: ({ children }) => <li>{children}</li>,
  code: ({ inline, children }) => inline ? <code className="bg-black/10 px-1 text-sm font-mono rounded">{children}</code> : <code className="block bg-black/10 p-2 text-sm font-mono rounded my-2">{children}</code>,
  a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--klein-blue))] underline">{children}</a>,
};

export default function ChatbotDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [chatbot, setChatbot] = useState(null);
  const [detailRatings, setDetailRatings] = useState(null);
  const [recentConvs, setRecentConvs] = useState([]);
  const [convLimit, setConvLimit] = useState(10);
  const [loadingMoreConvs, setLoadingMoreConvs] = useState(false);
  const [health, setHealth] = useState(null);
  const [notes, setNotes] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesDirty, setNotesDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [training, setTraining] = useState(false);
  const [copied, setCopied] = useState(false);
  const [testMessage, setTestMessage] = useState("");
  const [testResponse, setTestResponse] = useState("");
  const [testLastUser, setTestLastUser] = useState("");
  const [testing, setTesting] = useState(false);
  // Batch 18: cache stats
  const [cacheStats, setCacheStats] = useState(null);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [cacheClearing, setCacheClearing] = useState(false);
  const [cacheWarming, setCacheWarming] = useState(false);
  const [cachePruning, setCachePruning] = useState(false);
  const [costHistory, setCostHistory] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadChatbot();
  }, [id]);

  const loadChatbot = async () => {
    try {
      const res = await chatbotApi.get(id);
      try { setNotes(res?.data?.notes || ""); } catch {}
        try { const rr = await chatbotApi.ratings(id, 30); setDetailRatings(rr?.data || null); } catch {}
    try { const rc = await chatbotApi.recentConversations(id, convLimit); setRecentConvs(rc?.data?.items || []); } catch {}
      try { const hr = await chatbotApi.health(id); setHealth(hr?.data || null); } catch {}
      setChatbot(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      navigate("/my-chatbots");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      setNotesSaving(true);
      await chatbotApi.setNotes(id, notes);
      setNotesDirty(false);
      toast?.success?.("Notes saved");
    } catch (e) {
      toast?.error?.(formatApiError ? formatApiError(e) : "Failed to save notes");
    } finally {
      setNotesSaving(false);
    }
  };

  const handleTrain = async () => {
    setTraining(true);
    try {
      await chatbotApi.train(id);
      toast.success("Training started!");
      // Poll for status
      const interval = setInterval(async () => {
        const res = await chatbotApi.getStatus(id);
        setChatbot(prev => ({ ...prev, status: res.data.status }));
        if (res.data.status === "live" || res.data.status === "failed") {
          clearInterval(interval);
          setTraining(false);
          if (res.data.status === "live") {
            toast.success("Training complete! Your chatbot is now live.");
          } else {
            toast.error("Training failed. Check the error log.");
          }
        }
      }, 2000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setTraining(false);
    }
  };

  const handleTest = async () => {
    if (!testMessage.trim()) return;
    setTesting(true);
    setTestResponse("");
    setTestLastUser(testMessage);
    try {
      const res = await chatbotApi.chat(id, testMessage);
      setTestResponse(res.data.response);
      setTestMessage("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const SL_THEMES = ["classic","midnight","sunset","forest","bubble","neon","ember","royal","retro","mono_dots","rose_gold","ocean"];
  const [slLinks, setSlLinks] = useState([]);
  const [slTheme, setSlTheme] = useState("classic");
  const [slCustom, setSlCustom] = useState("");
  const [slCreating, setSlCreating] = useState(false);
  const loadShortLinks = useCallback(async () => {
    try { const r = await chatbotApi.listShortLinks(id); setSlLinks(r?.data?.items || []); } catch (e) {}
  }, [id]);
  useEffect(() => { loadShortLinks(); }, [loadShortLinks]);
  const createShortLink = async () => {
    setSlCreating(true);
    try {
      await chatbotApi.createShortLink(id, { theme: slTheme, slug: slCustom || undefined });
      setSlCustom("");
      await loadShortLinks();
      toast.success("Short link created!");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e)); }
    finally { setSlCreating(false); }
  };
  const deleteShortLink = async (linkId) => {
    try { await chatbotApi.deleteShortLink(linkId); await loadShortLinks(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success("Copied!"); };
  const copyEmbedCode = () => {
    const code = `<script src="${window.location.origin}/api/widget/${id}"></script>`;
    try {
      const textarea = document.createElement("textarea");
      textarea.value = code;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Embed code copied!");
    } catch {
      toast.error("Failed to copy. Please select and copy manually.");
    }
  };

  const getStatusBadge = (status) => {
    const classes = {
      untrained: "status-badge status-untrained",
      training: "status-badge status-training",
      live: "status-badge status-live",
      failed: "status-badge status-failed"
    };
    return <span className={classes[status] || classes.untrained}>{status}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!chatbot) return null;

  // BC-020: viewer and agent roles cannot see training, settings, or cache.
  const limitedRole = chatbot.access_role === "viewer" || chatbot.access_role === "agent";

  const loadCacheStats = async () => {
    if (!id) return;
    setCacheLoading(true);
    try {
      const res = await chatbotApi.cacheStats(id);
      setCacheStats(res.data || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load cache stats");
    } finally {
      setCacheLoading(false);
    }
  };

  const handleTtlEdit = async () => {
    if (!id) return;
    const current = cacheStats?.cache_ttl_hours || 24;
    const raw = window.prompt(`Set cache TTL in hours (1-720). Current: ${current}h`, String(current));
    if (raw === null) return;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 1 || n > 720) {
      toast.error("Enter a number between 1 and 720");
      return;
    }
    try {
      await chatbotApi.cacheUpdateSettings(id, { cache_ttl_hours: n });
      toast.success(`Cache TTL set to ${n}h`);
      await loadCacheStats();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to update TTL");
    }
  };

  const handleCacheClear = async () => {
    if (!id) return;
    if (!window.confirm("Clear all cached responses for this chatbot? This cannot be undone.")) return;
    setCacheClearing(true);
    try {
      const res = await chatbotApi.cacheClear(id);
      toast.success(`Cleared ${res.data?.deleted || 0} cached entries`);
      await loadCacheStats();
 loadCostHistory();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to clear cache");
    } finally {
      setCacheClearing(false);
    }
  };

  const handleCacheWarmup = async () => {
    if (!id) return;
    setCacheWarming(true);
    try {
      const res = await chatbotApi.cacheWarmup(id);
      const d = res?.data || {};
      if ((d.seeded || 0) > 0) {
        toast.success(`Seeded ${d.seeded} FAQ entries into cache.`);
      } else if ((d.total_faqs || 0) === 0) {
        toast.error("No FAQs to seed. Add some in Settings first.");
      } else {
        toast.message(`Seeded 0 entries (skipped ${d.skipped || 0}).`);
      }
      await loadCacheStats();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cache warmup failed");
    } finally {
      setCacheWarming(false);
    }
  };

  const handleCachePrune = async () => {
    if (!id) return;
    if (!window.confirm("Prune cache entries older than 30 days?")) return;
    setCachePruning(true);
    try {
      const res = await chatbotApi.cachePrune(id, 30);
      const d = res?.data || {};
      toast.success(`Pruned ${d.pruned || 0} stale cache entries.`);
      await loadCacheStats();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cache prune failed");
    } finally {
      setCachePruning(false);
    }
  };

  const loadCostHistory = async () => {
    if (!id) return;
    try {
      const res = await chatbotApi.cacheCostHistory(id, 14);
      setCostHistory(res?.data?.points || []);
    } catch (e) {
      // silent
    }
  };

  const loadMoreConvs = async () => {
    if (loadingMoreConvs) return;
    setLoadingMoreConvs(true);
    try {
      const next = Math.min(convLimit + 10, 50);
      const rc = await chatbotApi.recentConversations(id, next);
      setRecentConvs(rc?.data?.items || []);
      setConvLimit(next);
    } catch (e) {
      // silent
    } finally {
      setLoadingMoreConvs(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="chatbot-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/my-chatbots">
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">{chatbot.name}</h1>
            {getStatusBadge(chatbot.status)}
          </div>
          <p className="text-muted-foreground">{chatbot.description || "No description"}</p>
        </div>
        <div className="flex items-center gap-2">
          {!limitedRole && (
            <Link to={`/chatbot/${id}/training`}>
              <Button variant="outline" className="rounded-none" data-testid="training-btn">
                <Database className="w-4 h-4 mr-2" /> Training Data
              </Button>
            </Link>
          )}
          {!limitedRole && (
            <Link to={`/chatbot/${id}/settings`}>
              <Button variant="outline" className="rounded-none" data-testid="settings-btn">
                <Settings className="w-4 h-4 mr-2" /> Settings
              </Button>
            </Link>
          )}
          {chatbot.status === "untrained" && (
            <Button onClick={handleTrain} disabled={training} data-testid="train-btn">
              {training ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              {training ? "Training..." : "Train & Deploy"}
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className={`grid w-full max-w-2xl rounded-none ${limitedRole ? "grid-cols-5" : "grid-cols-6"}`}>
          <TabsTrigger value="overview" className="rounded-none">Overview</TabsTrigger>
          <TabsTrigger value="test" className="rounded-none">Test Chat</TabsTrigger>
          <TabsTrigger value="deploy" className="rounded-none">Deploy</TabsTrigger>
            <TabsTrigger value="ratings" className="rounded-none" data-testid="detail-tab-ratings">Ratings</TabsTrigger>
            <TabsTrigger value="convos" className="rounded-none" data-testid="detail-tab-convos">Conversations</TabsTrigger>
          {!limitedRole && (
            <TabsTrigger value="cache" className="rounded-none" data-testid="detail-tab-cache" onClick={() => { loadCacheStats(); loadCostHistory(); }}>Cache</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border">
            <div className="bg-background p-6">
              <p className="overline mb-2">Status</p>
              <div className="text-2xl font-bold capitalize">{chatbot.status}</div>
            </div>
            <div className="bg-background p-6">
              <p className="overline mb-2">LLM Provider</p>
              <div className="text-2xl font-bold capitalize">{chatbot.llm_provider}</div>
            </div>
            <div className="bg-background p-6">
              <p className="overline mb-2">Model</p>
              <div className="text-2xl font-bold">{chatbot.model_name}</div>
            </div>
          </div>

          <div className="border border-border p-6">
            <h3 className="font-semibold mb-4">System Prompt</h3>
            <p className="text-muted-foreground whitespace-pre-wrap">{chatbot.system_prompt}</p>
          </div>

          <div className="border border-border p-6">
            <h3 className="font-semibold mb-4">Configuration</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Temperature</p>
                <p className="font-medium">{chatbot.temperature}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Language</p>
                <p className="font-medium">{chatbot.language || "English"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Public</p>
                <p className="font-medium">{chatbot.is_public ? "Yes" : "No"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Created</p>
                <p className="font-medium">{new Date(chatbot.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
                  {health && (
            <div className="border border-border p-6 rounded" data-testid="health-card">
              <h3 className="font-semibold mb-4">Health</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><p className="text-muted-foreground">Status</p><p className="font-medium">{health.status || "unknown"}</p></div>
                <div><p className="text-muted-foreground">Documents</p><p className="font-medium">{health.documents != null ? health.documents : "—"}</p></div>
                <div><p className="text-muted-foreground">Vectors</p><p className="font-medium">{health.vectors != null ? health.vectors : "—"}</p></div>
                <div><p className="text-muted-foreground">Last trained</p><p className="font-medium">{health.last_trained ? new Date(health.last_trained).toLocaleDateString() : "—"}</p></div>
              </div>
            </div>
          )}
        <div className="border border-border p-6 rounded" data-testid="notes-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-medium">Internal notes</p>
                <p className="text-xs text-muted-foreground">Private notes visible only to your workspace.</p>
              </div>
              <button type="button" onClick={handleSaveNotes} disabled={notesSaving || !notesDirty} className="px-3 py-1.5 text-sm rounded bg-indigo-600 text-white disabled:opacity-50">{notesSaving ? "Saving…" : "Save notes"}</button>
            </div>
            <textarea value={notes} onChange={(e) => { setNotes(e.target.value); setNotesDirty(true); }} maxLength={5000} rows={5} placeholder="Add private notes about this chatbot…" className="w-full p-2 border border-border rounded text-sm font-mono" />
            <p className="text-[11px] text-muted-foreground mt-1">{notes.length}/5000</p>
          </div>
          </TabsContent>
          <TabsContent value="ratings" className="space-y-6">
            <SectionCard title="Recent Ratings" description="Visitor thumbs-up/down on AI replies (last 30 days)." data-testid="detail-ratings">
              {detailRatings ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className="p-3 border rounded-lg"><p className="text-xs text-slate-500">Total</p><p className="text-2xl font-semibold">{detailRatings.total}</p></div>
                    <div className="p-3 border rounded-lg"><p className="text-xs text-slate-500">Up</p><p className="text-2xl font-semibold text-emerald-600">{detailRatings.up}</p></div>
                    <div className="p-3 border rounded-lg"><p className="text-xs text-slate-500">Down</p><p className="text-2xl font-semibold text-rose-600">{detailRatings.down}</p></div>
                    <div className="p-3 border rounded-lg"><p className="text-xs text-slate-500">Score</p><p className="text-2xl font-semibold">{detailRatings.satisfaction_score !== null && detailRatings.satisfaction_score !== undefined ? `${detailRatings.satisfaction_score}%` : "--"}</p></div>
                  </div>
                  {detailRatings.recent_comments && detailRatings.recent_comments.length > 0 ? (
                    <div className="space-y-2" data-testid="detail-ratings-comments">
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Recent feedback</p>
                      {detailRatings.recent_comments.slice(0, 10).map((c, i) => (
                        <div key={i} className={`text-sm p-3 rounded-md border ${c.rating === "down" ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200"}`}>
                          <p className="text-xs text-slate-500 mb-1">{c.rating === "down" ? "👎" : "👍"} {c.created_at ? new Date(c.created_at).toLocaleString() : ""}</p>
                          <p className="text-slate-700">{c.comment}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">No comments yet.</p>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-500">No ratings yet. Visitors can rate AI messages from the chat widget.</p>
              )}
            </SectionCard>
          </TabsContent>

        <TabsContent value="convos" className="space-y-6">
          <SectionCard title="Recent conversations" description="Latest 10 conversations for this chatbot." padding="p-0">
            {(recentConvs && recentConvs.length > 0) ? (
              <div className="divide-y divide-slate-200">
                {recentConvs.map((c) => (
                  <Link key={c.id} to={`/conversations?conv=${c.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{c.visitor_data?.name || c.visitor_data?.email || "Anonymous visitor"}</div>
                      <div className="text-xs text-slate-500 truncate">{c.last_message || "(no messages yet)"}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">{c.message_count || 0} msg{c.message_count === 1 ? "" : "s"}</div>
                      <div className="text-xs text-slate-400">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}</div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-sm text-slate-500">No conversations yet for this chatbot.</div>
            )}
          
        {recentConvs && recentConvs.length > 0 && convLimit < 50 && (
          <div className="p-3 border-t border-border text-center">
            <button type="button" onClick={loadMoreConvs} disabled={loadingMoreConvs} className="text-sm text-blue-600 hover:underline disabled:opacity-50" data-testid="conv-load-more-btn">
              {loadingMoreConvs ? "Loading..." : `Load more (${recentConvs.length}/50)`}
            </button>
          </div>
        )}
        </SectionCard>
        </TabsContent>

        <TabsContent value="test" className="space-y-6">
          {chatbot.status !== "live" ? (
            <div className="text-center py-12 border border-dashed border-border">
              <MessageCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-semibold mb-2">Chatbot not ready</h3>
              <p className="text-muted-foreground mb-4">Train your chatbot first to test it</p>
              <Button onClick={handleTrain} disabled={training} data-testid="train-to-test-btn">
                {training ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                Start Training
              </Button>
            </div>
          ) : (
            <div className="border border-border">
              <div className="border-b border-border p-4">
                <h3 className="font-semibold">Test Your Chatbot</h3>
              </div>
              <div className="p-4 min-h-[200px]">
                {testResponse && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <div className="max-w-[80%] px-4 py-2 rounded-lg bg-blue-600 text-white text-sm">{testLastUser}</div>
            </div>
            <div className="flex justify-start">
              <div className="max-w-[80%] px-4 py-3 rounded-lg bg-muted text-sm">
                <p className="text-xs font-medium mb-1 text-slate-500">Bot</p>
                <div className="prose prose-sm max-w-none"><ReactMarkdown components={MarkdownComponents}>{testResponse}</ReactMarkdown></div>
              </div>
            </div>
          </div>
        )}
              </div>
              <div className="border-t border-border p-4">
                <div className="flex gap-2">
                  <Input
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    placeholder="Type a message..."
                    onKeyDown={(e) => e.key === "Enter" && handleTest()}
                    className="rounded-none"
                    data-testid="test-message-input"
                  />
                  <Button onClick={handleTest} disabled={testing} data-testid="send-test-btn">
                    {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="deploy" className="space-y-6">
          <div className="border border-border p-6">
            <h3 className="font-semibold mb-4">Embed Code</h3>
            <p className="text-muted-foreground mb-4">Add this code to your website to embed the chatbot widget.</p>
            <div className="bg-muted p-4 font-mono text-sm mb-4 break-all">
              {`<script src="${window.location.origin}/api/widget/${id}"></script>`}
            </div>
            <Button onClick={copyEmbedCode} variant="outline" data-testid="copy-embed-btn">
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? "Copied!" : "Copy Code"}
            </Button>
          </div>

          <div className="border border-border p-6">
            <h3 className="font-semibold mb-4">Public Link</h3>
            <p className="text-muted-foreground mb-4">Share this link to let users chat with your bot directly.</p>
            <div className="flex items-center gap-2">
              <Input
                value={`${window.location.origin}/chat/${id}`}
                readOnly
                className="rounded-none font-mono text-sm"
              />
              <Button
                variant="outline"
                onClick={() => window.open(`/chat/${id}`, "_blank")}
                data-testid="open-chat-btn"
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
            </div>
          </div>
        
        <div className="border border-border p-6" data-testid="shortlink-section">
          <h3 className="font-semibold mb-1">Short Link & QR Code</h3>
          <p className="text-muted-foreground mb-4 text-sm">Create a short link and QR code for this chatbot's public chat page.</p>
          <div className="flex flex-wrap items-end gap-2 mb-4">
            <div>
              <Label className="text-xs">QR theme</Label>
              <select value={slTheme} onChange={(e) => setSlTheme(e.target.value)} className="block border border-border bg-background px-2 py-2 text-sm rounded-none" data-testid="shortlink-theme">
                {SL_THEMES.map((t) => (<option key={t} value={t}>{t.replace("_", " ")}</option>))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Custom name (optional)</Label>
              <Input value={slCustom} onChange={(e) => setSlCustom(e.target.value)} placeholder="my-bot" className="rounded-none" data-testid="shortlink-custom" />
            </div>
            <Button className="rounded-none" onClick={createShortLink} disabled={slCreating} data-testid="create-shortlink-btn">{slCreating ? "Creating..." : "Generate"}</Button>
          </div>
          <div className="space-y-3">
            {slLinks.map((l) => (
              <div key={l.id} className="flex items-center gap-3 border border-border p-3" data-testid="shortlink-item">
                {l.qr_data_url ? <img src={l.qr_data_url} alt="QR" className="w-16 h-16" /> : null}
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm truncate">{l.short_url}</div>
                  <div className="text-xs text-muted-foreground">{l.clicks || 0} clicks &middot; theme: {l.qr_theme}</div>
                </div>
                <Button variant="outline" size="sm" className="rounded-none" onClick={() => copyText(l.short_url)} data-testid="copy-shortlink-btn"><Copy className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" className="rounded-none" onClick={() => { const a = document.createElement("a"); a.href = l.qr_data_url; a.download = (l.slug || "qr") + ".png"; a.click(); }} data-testid="download-qr-btn">QR</Button>
                <Button variant="outline" size="sm" className="rounded-none" onClick={() => deleteShortLink(l.id)} data-testid="delete-shortlink-btn">Delete</Button>
              </div>
            ))}
            {slLinks.length === 0 ? <p className="text-xs text-muted-foreground">No short links yet.</p> : null}
          </div>
        </div>
      </TabsContent>
        <TabsContent value="cache" className="space-y-6">
          <SectionCard title="Response Cache" subtitle="Cached answers served instantly without calling the LLM.">
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs text-muted-foreground">
                {cacheStats?.cache_enabled ? "✅ Cache enabled" : "⚠️ Cache disabled—enable in Settings."}
                {cacheStats?.cache_enabled && (<button type="button" onClick={handleTtlEdit} className="ml-3 underline decoration-dotted text-slate-600 hover:text-slate-900" title="Click to edit cache TTL" data-testid="cache-ttl-edit">TTL: {cacheStats?.cache_ttl_hours}h</button>)}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={loadCacheStats} disabled={cacheLoading} className="rounded-none" data-testid="cache-refresh-btn">
                  {cacheLoading ? "Loading..." : "Refresh"}
                </Button>
                <Button variant="outline" size="sm" onClick={handleCacheWarmup} disabled={cacheWarming} className="rounded-none" data-testid="cache-warmup-btn" title="Pre-seed cache with FAQ answers so the first visitor question is instant.">
                {cacheWarming ? "Warming..." : "Warm from FAQs"}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCachePrune} disabled={cachePruning || !cacheStats?.total_entries} className="rounded-none" data-testid="cache-prune-btn" title="Remove cache entries older than 30 days">
              {cachePruning ? "Pruning..." : "Prune Old"}
            </Button>
              <Button variant="outline" size="sm" onClick={handleCacheClear} disabled={cacheClearing || !cacheStats?.total_entries} className="rounded-none" data-testid="cache-clear-btn">
                  {cacheClearing ? "Clearing..." : "Clear All"}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="border border-border p-4">
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Entries</div>
                <div className="text-2xl font-semibold mt-1">{cacheStats?.total_entries ?? 0}</div>
              </div>
              <div className="border border-border p-4">
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Cache Hits</div>
                <div className="text-2xl font-semibold mt-1">{cacheStats?.total_hits ?? 0}</div>
              </div>
              <div className="border border-border p-4">
                <div className="text-xs text-muted-foreground uppercase tracking-wide">API Calls Saved</div>
                <div className="text-2xl font-semibold mt-1 text-emerald-600">{cacheStats?.estimated_api_calls_saved ?? 0}</div>
              </div>
          <div className="border border-border p-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Cost Saved</div>
            <div className="text-2xl font-semibold mt-1 text-emerald-600">${(((cacheStats?.estimated_api_calls_saved ?? 0) * 0.0015)).toFixed(4)}</div>
          </div>
            </div>
        {costHistory.length > 0 && (
          <div className="border border-border rounded-none p-4 mb-6" data-testid="cost-history-card">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Cost Saved (last 14 days)</div>
              <div className="text-sm font-mono">${costHistory.reduce((a,p)=>a+(p.saved_usd||0),0).toFixed(4)}</div>
            </div>
            <div className="flex items-end gap-1 h-16">
              {costHistory.map((p,i)=>{
                const max = Math.max(...costHistory.map(x=>x.hits||0), 1);
                const h = Math.max(2, Math.round(((p.hits||0)/max)*60));
                return (
                  <div key={i} className="flex-1 bg-emerald-500/60 hover:bg-emerald-500 transition-colors" style={{height: h+"px"}} title={`${p.date}: ${p.hits} hits, $${(p.saved_usd||0).toFixed(4)}`} />
                );
              })}
            </div>
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
              <span>{costHistory[0]?.date}</span>
              <span>{costHistory[costHistory.length-1]?.date}</span>
            </div>
          </div>
        )}
            <div>
              <h3 className="font-semibold text-sm mb-2">Top Cached Questions</h3>
              {(!cacheStats?.top_entries || cacheStats.top_entries.length === 0) ? (
                <div className="text-xs text-muted-foreground border border-dashed border-border p-6 text-center">
                  No cached questions yet. Ask the bot the same question twice to populate the cache.
                </div>
              ) : (
                <div className="border border-border divide-y divide-border">
                  {cacheStats.top_entries.map((e, i) => (
                    <div key={i} className="p-3 flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate" title={e.question}>{e.question}</div>
                        <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{e.response_preview}</div>
                      </div>
                      <div className="text-xs text-muted-foreground flex-shrink-0">
                        <div className="text-right"><span className="font-semibold text-emerald-600">{e.hits}</span> hits</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </SectionCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
